import parse from 'url-parse';
import { fg } from '@atlassian/jira-feature-gating';
import { sanitiseAssetName } from '@jira/platform__utils-sanitise-asset-name/src/index.js';
import { restUrlsToTrack } from './restUrlsToTrack.tsx';

const isWRMResource = (url: string) => url.includes('/batch.js') || url.includes('/batch.css');
const getWRMAssetName = (url: string) => {
	const [, resources] = /\/([\w.\-,:]*?)(?:,-|\/batch\.)/.exec(url) || [];
	const assetName = sanitiseAssetName(process.env.NODE_ENV !== 'production')(url);

	if (!resources) {
		return assetName;
	}

	return `wrm/${resources}/${assetName}`;
};
const getFileName = (url: string): string =>
	isWRMResource(url)
		? getWRMAssetName(url)
		: sanitiseAssetName(process.env.NODE_ENV !== 'production')(url);

// Original replacement chain, kept untouched for the feature-gate-off path.
const normalizePathLegacy = (pathname: string): string =>
	pathname
		.replace(/\/(project|agent)(-config|s)?\/([A-Z0-9]+)/g, '/$1$2/<project-key>')
		.replace(/\/(servicedesk)(-data)?\/([A-Z0-9]+)/g, '/$1$2/<project-key>')
		.replace(
			/\/(browse|automation|pagination|permissions|async|dialog|itsm)\/([A-Z0-9]+)/g,
			'/$1/<project-key>',
		)
		.replace(/\/(board)(s)?\/([0-9]+)/g, '/$1$2/<board-id>')
		.replace(/\/(\w+)-(\d+)\//i, '/<issue-key>/')
		.replace(/\/pq\/[a-fA-F0-9]+/i, '/pq/<id>') // persisted queries
		.replace(/\/user\/[a-fA-F0-9\-:]+/i, '/user/<user-id>') // engage targeting
		.replace(
			/[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}/gi,
			'<uuid>',
		)
		.replace(/\/\d+$/i, '/<id>');

// Consolidated replacement chain (see JIV-22404). All the segment-specific project-key
// replacements from the legacy chain above (project/agent/servicedesk/browse/automation/
// pagination/permissions/async/dialog/itsm) plus every segment discovered via UGC audits
// of production timings:resource URLs (projectlandingpage/epicIssueTypeIds/
// connectprojectconfig/roles) are collapsed into a single alternation instead of one
// `.replace()` per segment. Likewise, the mid-path and end-of-path issue-key
// replacements are collapsed into one pattern. This also fixes a pre-existing bug where
// the /user/ hex/colon character class could partially match literal path words that
// start with a hex-like letter (e.g. /user/assignable -> /user/<user-id>ssignable).
const normalizePathOptimized = (pathname: string): string =>
	pathname
		// Project/space key character class allows `_` and a leading digit: real keys
		// can contain underscores (e.g. BASE_TS, AMB_2339) or start with a digit (e.g.
		// 2LV, 3DPLUS). `_` counts as a \w character, so without it in the class the
		// trailing `\b` boundary check never succeeds and the whole match silently
		// fails, leaking the raw key. The `(?=[A-Z0-9_]*[A-Z])` lookahead requires at
		// least one letter somewhere in the value so a purely-numeric segment (a plain
		// id, not a key) is never swallowed here - see the servicedesk-direct pattern
		// below for why that distinction matters.
		.replace(
			/\/(project|agent|servicedesk|browse|automation|pagination|permissions|async|dialog|itsm|projectlandingpage|epicIssueTypeIds|connectprojectconfig|roles|kb|knowledge|createmeta|space)(-config|-data|s)?\/(?=[A-Z0-9_]*[A-Z])[A-Z0-9_]+\b/g,
			'/$1$2/<project-key>',
		)
		// Bare project key directly under /servicedesk/<id>/ with no "project" segment
		// name in between, e.g. /rest/servicedesk/2/CTECH/settings/feedback. The
		// lookahead above is what stops the numeric <id> here from being consumed as
		// the "project key" by the main alternation before this pattern gets to run.
		.replace(
			/\/servicedesk\/(\d+)\/(?=[A-Z0-9_]*[A-Z])[A-Z0-9_]+\b/g,
			'/servicedesk/$1/<project-key>',
		)
		.replace(/\/(board)(s)?\/([0-9]+)/g, '/$1$2/<board-id>')
		// Three-part test-management keys, e.g. MP-TC-4567 (Xray-style "<project>-TC-<id>").
		// Must run before the two-part issue-key replacement below: it wouldn't match this
		// shape anyway (the middle segment isn't purely digits), but keeping this first
		// avoids relying on that and keeps the "more specific pattern first" convention.
		.replace(/\/(\w+)-[A-Z]+-(\d+)(\/|$)/g, '/<issue-key>$3')
		// Issue key anywhere in the path, whether mid-path (trailing slash) or the final
		// segment. The numeric part may have a single leading letter for test-management
		// keys like APCONV-T2207 (test case) or SAP-R257 (test run).
		.replace(/\/(\w+)-([A-Z]?\d+)(\/|$)/g, '/<issue-key>$3')
		// Bare project key under /request/ once dash-number issue keys are already
		// resolved above, e.g. /rest/servicedeskapi/request/INFRA
		.replace(/\/request\/(?=[A-Z0-9_]*[A-Z])[A-Z0-9_]+\b/g, '/request/<project-key>')
		.replace(/\/pq\/[a-fA-F0-9]+/i, '/pq/<id>') // persisted queries
		.replace(/\/user\/[a-fA-F0-9\-:]+(?![a-z])/i, '/user/<user-id>') // engage targeting
		.replace(
			/[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}/gi,
			'<uuid>',
		)
		.replace(/\/\d+$/i, '/<id>')
		.replace(/\/noeyeball\/.*$/g, '/<noeyeball-stuff>')
		.replace(/\/issue\/[A-Z0-9\-_]+/g, '/issue/<issue-key>')
		// Colon-delimited project ids, e.g.
		// /universal_avatar/type/issuetype/owner/project:10275
		.replace(/project:\d+/g, 'project:<id>')
		.replace(/<project-key>.*?(\/|$)/g, '<project-key>/')
		.replace(/\/(analyticscontext|addons|board|properties|field|rule)\/.*?(\/|$)/g, '/$1/<value>/')
		// Slash-wrapped numeric/decimal ids. Uses a lookahead for the trailing slash
		// instead of consuming it, so consecutive numeric segments (e.g.
		// /dashboards/2/10101/gadget) each get their own match instead of the second
		// segment losing its leading slash to the first match.
		.replace(/\/[\d.]+(?=\/)/g, '/<id>')
		// Numeric filenames with an extension, e.g. /rest/dashboards/<id>/29830.json
		// (the trailing-id replacement above only matches when the digits are the very
		// last characters, so a ".json" suffix defeats it).
		.replace(/\/\d+\.(\w+)$/, '/<id>.$1')
		// Property/setting keys with a numeric id suffix baked in, e.g.
		// userProperties/viewSwitching-modifiedView-3238 or
		// upload-user-collection-1964945121. Runs last so plain PROJECT-123 shaped
		// segments are already resolved by the issue-key replacement above.
		.replace(/-\d{2,}(\/|$)/g, '-<id>$1');

const sanitiseEndpoints = (url: string): string => {
	const parsedUrl = parse(url, true);

	/**
	 * For endpoints with operation or operationName queryParam we will shorten the url
	 * as ufo pipeline will only get the `operation` query param for aggregation
	 */

	const { operation, operationName } = parsedUrl.query;
	let operationNameOverride = operation ?? operationName;

	// Normalize pathname with same replacements to match against restUrlsToTrack
	const normalizedPath = fg('jiv_22404_sanitise_extra_url_patterns')
		? normalizePathOptimized(parsedUrl.pathname)
		: normalizePathLegacy(parsedUrl.pathname);

	// Inject synthetic operation names for tracked endpoints when none provided.
	if (!operationNameOverride) {
		for (const { api, operationName: opName } of restUrlsToTrack) {
			if (normalizedPath.includes(api)) {
				operationNameOverride = opName;
				break;
			}
		}
	}

	if (operationNameOverride) {
		return `/${operationNameOverride}?operation=${operationNameOverride}`;
	}

	return normalizedPath;
};

export const jiraResourceTimingsConfig: {
	sanitiseEndpoints: (url: string) => string;
	mapResources: (url: string) => string;
	hasTimingHeaders: (url: string) => boolean;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	xhrFilter: (url: any) => any;
} = {
	sanitiseEndpoints,
	mapResources: getFileName,
	hasTimingHeaders: (url: string): boolean =>
		url.includes('jira-frontend-branch') ||
		url.includes('jira-frontend-bifrost') ||
		(fg('jfp_update_cdn_allowlist') ? url.includes('jira-fe') : false) ||
		isWRMResource(url),
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	xhrFilter: (url: any): any => url.includes('/rest/greenhopper/'),
};
